/**
 * Bookmark store — leaf module (Perf 3.1.b Step 5). Cross-store refs via runtime getStores().
 */
import { create } from 'zustand';
import { bookmarkFoldersApi } from '@/lib/api/bookmarks';
import { chatsApi } from '@/lib/api/chats';

const getStores = () => require('@/store');

const useBookmarkStore = create((set, get) => ({
  bookmarks: [],
  pagination: { current: 0, total: 0, hasMore: true, count: 0 },
  isLoading: false,
  hasInitiallyLoaded: false,

  // Filters
  activeFolderId: null, // null = "All Bookmarks"
  sort: 'newest',
  searchQuery: '',

  // Folders
  folders: [],
  foldersLoaded: false,

  fetchBookmarks: async (page = 1) => {
    if (!getStores().useAuthStore.getState().token) {
      set({ isLoading: false, hasInitiallyLoaded: true });
      return;
    }
    const { isLoading, activeFolderId, sort, searchQuery } = get();
    if (isLoading) return;
    set({ isLoading: true });
    try {
      const res = await chatsApi.fetchBookmarks(page, 20, { folderId: activeFolderId, sort, search: searchQuery || undefined });
      const incoming = res.bookmarks || [];
      set(state => ({
        bookmarks: page === 1 ? incoming : [...state.bookmarks, ...incoming],
        pagination: res.pagination || state.pagination,
        hasInitiallyLoaded: true,
      }));
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  setActiveFolderId: (folderId) => {
    set({ activeFolderId: folderId, bookmarks: [], hasInitiallyLoaded: false, pagination: { current: 0, total: 0, hasMore: true, count: 0 } });
    get().fetchBookmarks(1);
  },

  setSort: (sort) => {
    set({ sort, bookmarks: [], hasInitiallyLoaded: false, pagination: { current: 0, total: 0, hasMore: true, count: 0 } });
    get().fetchBookmarks(1);
  },

  setSearchQuery: (searchQuery) => {
    set({ searchQuery, bookmarks: [], hasInitiallyLoaded: false, pagination: { current: 0, total: 0, hasMore: true, count: 0 } });
    get().fetchBookmarks(1);
  },

  removeBookmark: (conversationId, messageId) => {
    set(state => ({
      bookmarks: state.bookmarks.filter(
        b => !(b.conversationId === conversationId && b.message?.id === messageId)
      ),
      pagination: {
        ...state.pagination,
        count: Math.max(0, state.pagination.count - 1),
      },
      folders: state.folders.map(f => {
        const had = f.items?.some(i => i.conversationId === conversationId && i.messageId === messageId);
        if (!had) return f;
        return {
          ...f,
          itemCount: Math.max(0, (f.itemCount || 0) - 1),
          items: f.items.filter(i => !(i.conversationId === conversationId && i.messageId === messageId)),
        };
      }),
    }));
  },

  // ── Folder actions ──
  fetchFolders: async () => {
    if (!getStores().useAuthStore.getState().token) {
      set({ folders: [], foldersLoaded: true });
      return;
    }
    try {
      const folders = await bookmarkFoldersApi.fetchFolders();
      set({ folders, foldersLoaded: true });
    } catch (error) {
      console.error('Error fetching bookmark folders:', error);
    }
  },

  createFolder: async (name, icon, parentId = null) => {
    try {
      const folder = await bookmarkFoldersApi.createFolder(name, icon, parentId);
      set(state => ({ folders: [folder, ...state.folders] }));
      return folder;
    } catch (error) {
      console.error('Error creating bookmark folder:', error);
      throw error;
    }
  },

  moveFolder: async (folderId, parentId) => {
    try {
      const updated = await bookmarkFoldersApi.updateFolder(folderId, { parentId });
      set(state => ({ folders: state.folders.map(f => f.id === folderId ? updated : f) }));
      return updated;
    } catch (error) {
      console.error('Error moving bookmark folder:', error);
      throw error;
    }
  },

  renameFolder: async (folderId, name) => {
    try {
      const updated = await bookmarkFoldersApi.updateFolder(folderId, { name });
      set(state => ({ folders: state.folders.map(f => f.id === folderId ? updated : f) }));
    } catch (error) {
      console.error('Error renaming bookmark folder:', error);
      throw error;
    }
  },

  updateFolderIcon: async (folderId, icon) => {
    try {
      const updated = await bookmarkFoldersApi.updateFolder(folderId, { icon });
      set(state => ({ folders: state.folders.map(f => f.id === folderId ? updated : f) }));
    } catch (error) {
      console.error('Error updating bookmark folder icon:', error);
      throw error;
    }
  },

  deleteFolder: async (folderId) => {
    try {
      const folder = get().folders.find(f => f.id === folderId);
      const nextParentId = folder?.parentId || null;
      await bookmarkFoldersApi.deleteFolder(folderId);
      const { activeFolderId } = get();
      set(state => ({
        folders: state.folders
          .filter(f => f.id !== folderId)
          .map(f => (f.parentId === folderId ? { ...f, parentId: nextParentId } : f)),
        ...(activeFolderId === folderId ? { activeFolderId: null } : {}),
      }));
      if (get().activeFolderId === null) get().fetchBookmarks(1);
    } catch (error) {
      console.error('Error deleting bookmark folder:', error);
      throw error;
    }
  },

  addToFolder: async (folderId, conversationId, messageId) => {
    try {
      const updated = await bookmarkFoldersApi.addItem(folderId, conversationId, messageId);
      set(state => ({ folders: state.folders.map(f => f.id === folderId ? updated : f) }));
    } catch (error) {
      console.error('Error adding bookmark to folder:', error);
      throw error;
    }
  },

  removeFromFolder: async (folderId, conversationId, messageId) => {
    try {
      const updated = await bookmarkFoldersApi.removeItem(folderId, conversationId, messageId);
      set(state => ({
        folders: state.folders.map(f => f.id === folderId ? updated : f),
        ...(state.activeFolderId === folderId
          ? { bookmarks: state.bookmarks.filter(b => !(b.conversationId === conversationId && b.message?.id === messageId)) }
          : {}),
      }));
    } catch (error) {
      console.error('Error removing bookmark from folder:', error);
      throw error;
    }
  },

  reset: () => set({ bookmarks: [], pagination: { current: 0, total: 0, hasMore: true, count: 0 }, isLoading: false, hasInitiallyLoaded: false, activeFolderId: null, sort: 'newest', folders: [], foldersLoaded: false }),
}));

export { useBookmarkStore };
export default useBookmarkStore;
